import { Button } from "newui/components/Button";
import { Flex } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import {
	formValuesGenerator,
	validationSchema,
} from "../../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/createRFQUtils";
import { NotFound } from "newui/features/ErrorPages/NotFound";
import { RFQTabNavigation } from "../../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/RFQTabNavigation";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import ContinueButton from "../../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/useContinueButton";
import { CreateRFxHeaderButtons } from "../../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/CreateRFxHeaderButtons";
import SavingOverlay from "newui/components/SavingOverlay";

/**
 * Shared layout shell for CreateRFQPage and CreateRFxTemplate.
 *
 * Owns the structural wrapper (NotFound guard, header buttons, tab navigation,
 * Formik provider, loading spinner, back/continue buttons) and delegates
 * page-specific content via render props.
 */
const RfxCreationLayout = ({
	// Shared hook state
	isNotFound,
	selectedTab,
	formikRef,
	isSubmittingForm,
	isValidForm,
	setIsValidForm,
	completedSteps,
	touchedTabs,
	tabRef,
	isFileUploading,
	tabButtonList,
	lastStep,
	enableOnChangeFormik,
	onBackButtonClicked,
	handleTabChangeWithSave,
	handleSubmitOutside,

	// Page configuration
	isTemplate,
	isEdit,
	hasLoaded,
	initialBlockSendingRFx,
	rfxData,
	companyId,

	// Header-specific overrides
	headerProps,

	// Tab navigation overrides
	tabNavProps,

	// Render slots
	renderTabContent,
	renderFooterPrefix,
	renderAfterForm,
	renderAfterContent,
}) => {
	return (
		<>
			{isNotFound && <NotFound />}
			{!isNotFound && (
				<div className="relative pt-4">
					<div ref={tabRef}>
						<CreateRFxHeaderButtons
							company_id={companyId}
							disableLastStep={initialBlockSendingRFx}
							handleSubmitOutside={handleSubmitOutside}
							isEdit={isEdit}
							isLoading={!hasLoaded}
							isFileUploading={isFileUploading}
							isSubmittingForm={isSubmittingForm}
							isTemplate={isTemplate}
							isValidForm={isValidForm}
							lastStep={lastStep}
							rfqData={rfxData}
							{...headerProps}
						/>
					</div>
					<div id="create-rfq-content" className="p-6">
						<RFQTabNavigation
							isLoading={!hasLoaded || !rfxData}
							tabButtonList={tabButtonList}
							selectedTab={selectedTab}
							completedSteps={completedSteps}
							touchedTabs={touchedTabs}
							formikRef={formikRef}
							isTemplate={isTemplate}
							isEdit={isEdit}
							rfqData={rfxData}
							onChangeTab={handleTabChangeWithSave}
							isQuoteValid={rfxData?.isQuoteTabValid}
						/>

						<SavingOverlay isShown={isSubmittingForm} />

						{hasLoaded ? (
							<Formik
								onSubmit={() => { }}
								initialValues={formValuesGenerator(
									isTemplate ? (rfxData ?? null) : rfxData,
									isTemplate
								)}
								innerRef={formikRef}
								validationSchema={validationSchema(isTemplate, rfxData?.rfxType)}
								validateOnChange={enableOnChangeFormik}
								validateOnMount={true}
							>
								{({ isValid, setFieldError, setFieldValue }) => {
									let blockSendingRFx = initialBlockSendingRFx || (!isValid && lastStep);
									if (isValid !== isValidForm) {
										setIsValidForm(isValid);
									}
									return (
										<Form>
											{renderTabContent({ setFieldError, setFieldValue })}
											<Flex gap={4} align={"center"} className="float-end py-10">
												{renderFooterPrefix && renderFooterPrefix()}
												{selectedTab > 1 && (
													<Button
														disabled={isSubmittingForm || isFileUploading}
														btntype={"base"}
														type="button"
														onClick={onBackButtonClicked}
													>
														Back
													</Button>
												)}
												<ContinueButton
													onClick={() => handleSubmitOutside(lastStep ? "laststep" : "next")}
													disabled={blockSendingRFx}
													isTemplate={isTemplate}
													lastStep={lastStep}
												/>
											</Flex>
											{renderAfterForm && renderAfterForm()}
										</Form>
									);
								}}
							</Formik>
						) : (
							<div className="min-h-[70vh]">
								<LoadingSpinner label={"Loading..."} />
							</div>
						)}

						{renderAfterContent && renderAfterContent()}
					</div>
				</div>
			)}
		</>
	);
};

export default RfxCreationLayout;
